import { useState, useEffect } from "react";
import { useDataStore } from "@store/user_org";
import { organizationsApi } from "@services/api/organizations";
import { showNotification } from "context/notification.service";
import { SwitchBrick } from "@components/ui/selection/SwitchBrick";
import TextField from "@components/ui/textfields/TextField";
import Button from "@components/ui/buttons/Button";
import SecondaryButton from "@components/ui/buttons/SecondaryButton";
import Icon1 from "@assets/icons/Icon";

export function ThreeCXSettings() {
  const orgData = useDataStore((s) => s.orgData);
  const orgMembers = useDataStore((s) => s.orgMembers);
  const fetchOrgData = useDataStore((s) => s.fetchOrgData);

  const tcx = orgData?.threecx;

  const [enabled, setEnabled] = useState(tcx?.enabled || false);
  const [serverUrl, setServerUrl] = useState(tcx?.server_url || "");
  const [apiKey, setApiKey] = useState(tcx?.api_key || "");
  const [webhookSecret, setWebhookSecret] = useState(tcx?.webhook_secret || "");
  const [userExtensions, setUserExtensions] = useState<Record<string, string>>(
    tcx?.user_extensions || {},
  );
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState<{
    success: boolean;
    message: string;
  } | null>(null);

  useEffect(() => {
    if (tcx) {
      setEnabled(tcx.enabled || false);
      setServerUrl(tcx.server_url || "");
      setApiKey(tcx.api_key || "");
      setWebhookSecret(tcx.webhook_secret || "");
      setUserExtensions(tcx.user_extensions || {});
    }
  }, [
    tcx?.enabled,
    tcx?.server_url,
    tcx?.api_key,
    tcx?.webhook_secret,
    JSON.stringify(tcx?.user_extensions),
  ]);

  const handleSave = async () => {
    if (!orgData) return;
    setSaving(true);
    try {
      const cleanExtensions: Record<string, string> = {};
      for (const [userId, ext] of Object.entries(userExtensions)) {
        if (ext.trim()) cleanExtensions[userId] = ext.trim();
      }

      await organizationsApi.update(orgData.id, {
        threecx: {
          enabled,
          server_url: serverUrl || undefined,
          api_key: apiKey || undefined,
          webhook_secret: webhookSecret || undefined,
          user_extensions:
            Object.keys(cleanExtensions).length > 0
              ? cleanExtensions
              : undefined,
        },
      } as any);
      await fetchOrgData(orgData.id);
      showNotification({
        type: "success",
        message: "Configuration 3CX sauvegardée",
      });
    } catch {
      showNotification({
        type: "error",
        message: "Erreur lors de la sauvegarde",
      });
    } finally {
      setSaving(false);
    }
  };

  const handleGenerateSecret = () => {
    const array = new Uint8Array(24);
    crypto.getRandomValues(array);
    const secret = Array.from(array, (b) =>
      b.toString(36).padStart(2, "0"),
    ).join("");
    setWebhookSecret(secret);
  };

  const handleTestConnection = async () => {
    if (!serverUrl || !apiKey) {
      setTestResult({
        success: false,
        message: "URL du serveur et clé API requises",
      });
      return;
    }
    setTesting(true);
    setTestResult(null);
    try {
      const { default: api } = await import("@services/api/client");
      const { data } = await api.post("/3cx/test-connection", {
        server_url: serverUrl,
        api_key: apiKey,
      });
      setTestResult({
        success: data.connected,
        message: data.connected
          ? `Connecté — ${data.version || "3CX"}`
          : data.detail || "Échec de connexion",
      });
    } catch (e: any) {
      setTestResult({
        success: false,
        message: e?.response?.data?.detail || e.message || "Erreur de connexion",
      });
    } finally {
      setTesting(false);
    }
  };

  const hasChanges =
    enabled !== (tcx?.enabled || false) ||
    serverUrl !== (tcx?.server_url || "") ||
    apiKey !== (tcx?.api_key || "") ||
    webhookSecret !== (tcx?.webhook_secret || "") ||
    JSON.stringify(userExtensions) !==
      JSON.stringify(tcx?.user_extensions || {});

  const webhookUrl = webhookSecret
    ? `${window.location.origin.replace("localhost:5173", "api.heric.fr")}/3cx/webhook/${webhookSecret}`
    : "";

  const lookupUrl = webhookSecret
    ? `${window.location.origin.replace("localhost:5173", "api.heric.fr")}/3cx/lookup/${webhookSecret}?phone=[Phone]`
    : "";

  return (
    <div className="flex flex-col p-8 max-w-2xl">
      <h1
        className="text-hx font-semibold mb-2"
        style={{ fontFamily: "Poppins" }}
      >
        Téléphonie 3CX
      </h1>
      <p
        className="text-h2 text-neutral-500 mb-8"
        style={{ fontFamily: "Poppins" }}
      >
        Identification du client à l'appel + ajout d'un évènement (avec
        transcription) sur le fil de l'ordre quand 3CX termine un appel.
      </p>

      {/* Connection status */}
      <div
        className="flex flex-row items-center gap-4 p-4 rounded-xl mb-6"
        style={{
          background: tcx?.connected ? "#F0FDF4" : "#FEF2F2",
          border: `1px solid ${tcx?.connected ? "#BBF7D0" : "#FECACA"}`,
        }}
      >
        <div
          className="w-10 h-10 rounded-full flex items-center justify-center"
          style={{ background: tcx?.connected ? "#DCFCE7" : "#FEE2E2" }}
        >
          <Icon1
            name={tcx?.connected ? "done2" : "warning"}
            size={20}
            color={tcx?.connected ? "#16A34A" : "#DC2626"}
          />
        </div>
        <div className="flex flex-col flex-1">
          <span
            className="text-h2 font-medium"
            style={{ color: tcx?.connected ? "#166534" : "#991B1B" }}
          >
            {tcx?.connected ? "3CX connecté" : "3CX non configuré"}
          </span>
          <span className="text-h4 text-neutral-500">
            {tcx?.connected
              ? `Serveur : ${tcx.server_url}`
              : "Renseignez l'URL et la clé API de votre serveur 3CX"}
          </span>
        </div>
      </div>

      {/* Enable toggle */}
      <div className="mb-6">
        <SwitchBrick
          checked={enabled}
          onChange={(v) => setEnabled(v)}
          title="Activer l'intégration 3CX"
          description="Active le click-to-call, la remontée de fiche et les notifications d'appels"
        />
      </div>

      {/* Server credentials */}
      <div
        className="flex flex-col gap-4 p-5 rounded-xl mb-6"
        style={{ background: "#FAFAFA", border: "1px solid #E5E7EB" }}
      >
        <span className="text-h1 font-semibold text-neutral-800">
          Connexion au serveur
        </span>
        <TextField
          label="URL du serveur 3CX"
          value={serverUrl}
          onChange={setServerUrl}
          placeholder="https://mongarage.3cx.fr"
        />
        <TextField
          label="Clé API (WebAPI Full Key)"
          value={apiKey}
          onChange={setApiKey}
          type="password"
        />
        <span className="text-h4 text-neutral-400 -mt-2">
          La clé API se trouve dans le panneau d'administration 3CX sous
          Intégrations &gt; API.
        </span>

        {/* Test connection */}
        <div className="flex flex-row items-center gap-2 pt-1">
          <SecondaryButton
            label={testing ? "Test en cours..." : "Tester la connexion"}
            onClick={handleTestConnection}
            disabled={testing || !serverUrl || !apiKey}
          />
        </div>
        {testResult && (
          <div
            className="p-3 rounded-lg border text-h3"
            style={{
              background: testResult.success ? "#F0FDF4" : "#FEF2F2",
              borderColor: testResult.success ? "#BBF7D0" : "#FECACA",
              color: testResult.success ? "#166534" : "#991B1B",
            }}
          >
            <div className="font-medium">
              {testResult.success ? "Connexion OK" : "Échec de connexion"}
            </div>
            <div className="text-h4 mt-1 opacity-80">{testResult.message}</div>
          </div>
        )}
      </div>

      {/* Webhook configuration */}
      <div
        className="flex flex-col gap-4 p-5 rounded-xl mb-6"
        style={{ background: "#FAFAFA", border: "1px solid #E5E7EB" }}
      >
        <span className="text-h1 font-semibold text-neutral-800">
          Webhook (réception des appels)
        </span>
        <span className="text-h3 text-neutral-500">
          Configurez ces URLs dans votre serveur 3CX pour recevoir les
          événements d'appels dans Heric.
        </span>

        <div className="flex flex-row items-end gap-2">
          <div className="flex-1">
            <TextField
              label="Secret du webhook"
              value={webhookSecret}
              onChange={setWebhookSecret}
              placeholder="Générez un secret"
            />
          </div>
          <button
            onClick={handleGenerateSecret}
            className="mb-1 px-3 py-2 rounded-lg border border-neutral-200 text-h3 text-neutral-600 hover:bg-neutral-100"
          >
            Générer
          </button>
        </div>

        {webhookUrl && (
          <div className="flex flex-col gap-2">
            <div className="flex flex-col gap-1">
              <span className="text-h4 font-medium text-neutral-600">
                URL du webhook (à configurer dans 3CX)
              </span>
              <div
                className="flex flex-row items-center gap-2 p-2 rounded-lg bg-white border border-neutral-200 cursor-pointer hover:bg-neutral-50"
                onClick={() => {
                  navigator.clipboard.writeText(webhookUrl);
                  showNotification({
                    type: "success",
                    message: "URL copiée",
                  });
                }}
              >
                <span className="text-h4 text-neutral-700 break-all flex-1 font-mono">
                  {webhookUrl}
                </span>
                <Icon1 name="copy" size={14} color="#9CA3AF" />
              </div>
            </div>

            <div className="flex flex-col gap-1">
              <span className="text-h4 font-medium text-neutral-600">
                URL de lookup CRM (identification des appelants)
              </span>
              <div
                className="flex flex-row items-center gap-2 p-2 rounded-lg bg-white border border-neutral-200 cursor-pointer hover:bg-neutral-50"
                onClick={() => {
                  navigator.clipboard.writeText(lookupUrl);
                  showNotification({
                    type: "success",
                    message: "URL copiée",
                  });
                }}
              >
                <span className="text-h4 text-neutral-700 break-all flex-1 font-mono">
                  {lookupUrl}
                </span>
                <Icon1 name="copy" size={14} color="#9CA3AF" />
              </div>
            </div>
          </div>
        )}
      </div>

      {/* User-extension mapping */}
      <div
        className="flex flex-col gap-4 p-5 rounded-xl mb-6"
        style={{ background: "#FAFAFA", border: "1px solid #E5E7EB" }}
      >
        <span className="text-h1 font-semibold text-neutral-800">
          Mapping collaborateurs
        </span>
        <span className="text-h4 text-neutral-400 -mb-2">
          Associez chaque collaborateur à son numéro d'extension 3CX. Cela
          permet le click-to-call et l'identification des appels par
          collaborateur.
        </span>

        {orgMembers
          .filter((m) => m.status !== "inactive")
          .map((member) => {
            const name = member.infos?.display_name || member.infos?.email || "";
            const photo = member.customization?.photo_url;
            return (
              <div key={member.id} className="flex flex-row items-center gap-3">
                <div className="flex flex-row items-center gap-2 flex-1 min-w-0">
                  {photo ? (
                    <img
                      src={photo}
                      className="w-8 h-8 rounded-full object-cover shrink-0"
                    />
                  ) : (
                    <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center text-h4 font-medium text-blue-600 shrink-0">
                      {(name || "?").charAt(0).toUpperCase()}
                    </div>
                  )}
                  <div className="flex flex-col min-w-0">
                    <span className="text-h3 font-medium text-neutral-800 truncate">
                      {name}
                    </span>
                    <span className="text-h4 text-neutral-400 truncate">
                      {member.role || ""}
                    </span>
                  </div>
                </div>
                <div className="w-[100px] shrink-0">
                  <TextField
                    label=""
                    value={userExtensions[member.id] || ""}
                    onChange={(v) =>
                      setUserExtensions((prev) => ({ ...prev, [member.id]: v }))
                    }
                    placeholder="Ex: 101"
                  />
                </div>
              </div>
            );
          })}

        {orgMembers.filter((m) => m.status !== "inactive").length === 0 && (
          <span className="text-h3 text-neutral-400 py-2">
            Aucun collaborateur actif
          </span>
        )}
      </div>

      {/* Save button */}
      <div className="flex flex-row items-center gap-3">
        <Button
          label={saving ? "Sauvegarde..." : "Sauvegarder"}
          onClick={handleSave}
          disabled={saving || !hasChanges}
        />
        {hasChanges && (
          <span className="text-h4 text-orange-500">
            Modifications non sauvegardées
          </span>
        )}
      </div>
    </div>
  );
}
